import { useState } from "react";
import { Zap, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { TOPUP_PRODUCTS } from "@/lib/credit-calculator";

interface TopupPurchaseProps {
  onPurchaseComplete?: () => void;
}

// These will be created in Stripe and the IDs stored in the database
// For now, using placeholder IDs that will be replaced when products are created
const TOPUP_PRICE_IDS: Record<string, string> = {
  "Small": "price_1T5SnUPJ0ZCzH9fviU5UF3gB",
  "Medium": "price_1T5SnkPJ0ZCzH9fvRFxrs5xf",
  "Large": "price_1T5SnxPJ0ZCzH9fv8POFS1Iq",
  "Bulk": "price_1T5SoHPJ0ZCzH9fv3cUCMTBt",
};

export function TopupPurchase({ onPurchaseComplete }: TopupPurchaseProps) {
  const { purchaseTopup, tier } = useSubscription();
  const [loadingProduct, setLoadingProduct] = useState<string | null>(null);

  const handlePurchase = async (productName: string, priceId: string) => {
    setLoadingProduct(productName);
    try {
      const url = await purchaseTopup(priceId);
      if (url) {
        window.open(url, "_blank");
        onPurchaseComplete?.();
      }
    } finally {
      setLoadingProduct(null);
    }
  };

  if (tier === "free") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Credit Top-Ups
          </CardTitle>
          <CardDescription>
            Upgrade to a paid plan to purchase additional credits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Credit top-ups are available for Solo, Startup, and Business plan subscribers.
            Top-up credits never expire (valid for 12 months) and are used after your monthly credits.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-primary" />
          Buy More Credits
        </CardTitle>
        <CardDescription>
          Top-up credits are valid for 12 months and used after your monthly credits
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid sm:grid-cols-2 gap-4">
          {TOPUP_PRODUCTS.map((product, index) => {
            const priceId = TOPUP_PRICE_IDS[product.name];
            const isLoading = loadingProduct === product.name;
            const isBestValue = index === TOPUP_PRODUCTS.length - 1;
            
            return (
              <div 
                key={product.name}
                className={`relative p-4 rounded-lg border-2 transition-colors ${
                  isBestValue 
                    ? "border-primary bg-primary/5" 
                    : "border-border hover:border-primary/50"
                }`}
              >
                {isBestValue && (
                  <Badge className="absolute -top-2 left-4 bg-primary">
                    Best Value
                  </Badge>
                )}
                
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold">{product.name}</h4>
                    <p className="text-2xl font-bold">
                      {product.credits}
                      <span className="text-sm font-normal text-muted-foreground ml-1">
                        credits
                      </span>
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      ${(product.priceCents / 100).toFixed(0)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ${product.perCreditCost.toFixed(2)}/credit
                    </span>
                  </div>
                  
                  <Button
                    onClick={() => handlePurchase(product.name, priceId)}
                    disabled={isLoading || !priceId}
                    variant={isBestValue ? "default" : "outline"}
                    className="w-full"
                    size="sm"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Buy Now
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        
        <p className="text-xs text-muted-foreground mt-4 text-center">
          Secure payment powered by Stripe. Credits are added instantly after purchase.
        </p>
      </CardContent>
    </Card>
  );
}
